"use client";

import { Pause, Play, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/** The speed button steps through these, X's order. */
export const PLAYBACK_RATES = [1, 1.25, 1.5, 2, 0.5] as const;

type ViewerVideoControlsProps = {
  video: HTMLVideoElement | null;
  /** What the video plays at now, a held or locked 2x included. */
  rate: number;
  muted: boolean;
  onCycleRate: () => void;
  onToggleMute: () => void;
};

const formatClock = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = String(whole % 60).padStart(2, "0");
  return `${minutes}:${rest}`;
};

const formatRate = (rate: number) => `${rate}x`;

/**
 * X's bottom bar for the active video: a scrubbable progress line, then
 * play/pause, time left, speed and sound. The progress fill is written to the
 * node every frame; only the clock goes through React (a few times a second).
 */
export function ViewerVideoControls({
  video,
  rate,
  muted,
  onCycleRate,
  onToggleMute,
}: ViewerVideoControlsProps) {
  const fillRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const scrubbingRef = useRef(false);
  const [isScrubbing, setScrubbing] = useState(false);
  const [paused, setPaused] = useState(true);
  const [ended, setEnded] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!video) return;
    let frame: number | null = null;

    const paint = () => {
      const fill = fillRef.current;
      if (fill && video.duration > 0 && !scrubbingRef.current) {
        fill.style.transform = `scaleX(${video.currentTime / video.duration})`;
      }
    };
    const loop = () => {
      paint();
      frame = video.paused ? null : requestAnimationFrame(loop);
    };
    const sync = () => {
      setPaused(video.paused);
      setEnded(video.ended);
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
      setRemaining(
        Number.isFinite(video.duration)
          ? video.duration - video.currentTime
          : 0,
      );
      paint();
      if (!video.paused && frame === null) frame = requestAnimationFrame(loop);
    };

    const events = [
      "play",
      "pause",
      "ended",
      "timeupdate",
      "durationchange",
      "loadedmetadata",
      "seeked",
    ];
    for (const name of events) video.addEventListener(name, sync);
    sync();
    return () => {
      for (const name of events) video.removeEventListener(name, sync);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [video]);

  const seekToPointer = (clientX: number) => {
    const track = trackRef.current;
    if (!track || !video || !(video.duration > 0)) return;
    const bounds = track.getBoundingClientRect();
    const ratio = Math.min(
      1,
      Math.max(0, (clientX - bounds.left) / bounds.width),
    );
    if (fillRef.current) fillRef.current.style.transform = `scaleX(${ratio})`;
    video.currentTime = ratio * video.duration;
    setRemaining(video.duration * (1 - ratio));
  };

  const togglePlay = () => {
    if (!video) return;
    if (video.paused) void video.play().catch(() => {});
    else video.pause();
  };

  const PlayIcon = ended ? RotateCcw : paused ? Play : Pause;

  return (
    <div className="flex flex-col gap-1">
      {/* 32px of touch target around a 3px line. */}
      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(duration - remaining)}
        aria-valuetext={`${formatClock(duration - remaining)} of ${formatClock(duration)}`}
        className="group relative -mx-1 flex h-8 cursor-pointer touch-none items-center px-1"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          scrubbingRef.current = true;
          setScrubbing(true);
          seekToPointer(event.clientX);
        }}
        onPointerMove={(event) => {
          if (scrubbingRef.current) seekToPointer(event.clientX);
        }}
        onPointerUp={() => {
          scrubbingRef.current = false;
          setScrubbing(false);
        }}
        onPointerCancel={() => {
          scrubbingRef.current = false;
          setScrubbing(false);
        }}
        onKeyDown={(event) => {
          if (!video) return;
          if (event.key === "ArrowRight") video.currentTime += 5;
          if (event.key === "ArrowLeft") video.currentTime -= 5;
        }}
      >
        <div
          className={cn(
            "relative w-full overflow-hidden rounded-full bg-white/25 transition-[height] duration-150 ease-out-strong",
            isScrubbing ? "h-[5px]" : "h-[3px]",
          )}
        >
          <div
            ref={fillRef}
            className="absolute inset-0 origin-left scale-x-0 bg-white"
          />
        </div>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={togglePlay}
          className="-ml-2 flex size-10 items-center justify-center rounded-full text-white transition-transform duration-150 ease-out-strong active:scale-90"
          aria-label={ended ? "Replay" : paused ? "Play" : "Pause"}
        >
          <PlayIcon
            className="size-[22px]"
            fill={ended ? "none" : "currentColor"}
            strokeWidth={ended ? 2 : 0}
          />
        </button>
        <span className="text-[13px] font-medium text-white/85 tabular-nums">
          -{formatClock(remaining)}
        </span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onCycleRate}
          className="flex h-10 min-w-12 items-center justify-center rounded-full px-2 text-[15px] font-semibold text-white tabular-nums transition-transform duration-150 ease-out-strong active:scale-90"
          aria-label={`Playback speed ${formatRate(rate)}`}
        >
          {formatRate(rate)}
        </button>
        <button
          type="button"
          onClick={onToggleMute}
          className="-mr-2 flex size-10 items-center justify-center rounded-full text-white transition-transform duration-150 ease-out-strong active:scale-90"
          aria-label={muted ? "Turn sound on" : "Turn sound off"}
          aria-pressed={!muted}
        >
          {muted ? (
            <VolumeX className="size-[21px]" />
          ) : (
            <Volume2 className="size-[21px]" />
          )}
        </button>
      </div>
    </div>
  );
}
